import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import {
  DocumentHistoryItem,
  DocumentTab,
  SearchHistoryItem,
} from 'features/knowledgeDocumentViewer/types'
import { DocumentViewerAssetType } from 'features/knowledgeDocumentViewer/types/ocrViewer'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { getSystemDocumentBqId } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import {
  allCandidatesHaveRevisionCodes,
  DEFAULT_TARGET_PAGE,
  getLatestCandidateByRevision,
  ReferenceResolverStrategy,
  resolveKnowledgeDocumentTargetPage,
  resolveOffPageConnectorTargets,
} from 'features/knowledgeDocumentViewer/utils/navigationTargetResolver'
import { ocrResultMatchKey } from 'features/knowledgeDocumentViewer/utils/viewerOcrBoxId'
import { useCallback, useMemo } from 'react'

import { useOcrRefsCount } from './useOcrRefsCount'
import { OcrResultWithPage, useOcrResults } from './useOcrResults'
import { useSystemDocumentsByBqIds } from './useSystemDocumentsByBqIds'

export type LinkedDocumentsNavigationParams = {
  ocrResult: OcrResultWithPage
  forceNewTab: boolean
  showReferencesTableOnSelect?: boolean
}

export type LinkedDocumentsNavigationResult = {
  documentIds: string[]
  referenceText: string
}

export type LinkedDocumentsNavigationOptions = {
  offPageConnectorDocuments?: SystemDocumentEntry[]
  isLoadingOffPageConnector?: boolean
  onNavigationSuccess?: (result: LinkedDocumentsNavigationResult) => void
  /** Strategy for resolving OPC / drawing-number references. Defaults to legacy string-match. */
  resolverStrategy?: ReferenceResolverStrategy
}

export const useLinkedDocumentsNavigation = (
  options: LinkedDocumentsNavigationOptions
) => {
  const {
    activeTabId,
    setPagination: setGlobalPagination,
    pushToHistory,
    addDocumentTab,
    addTab,
  } = useKnowledgeDocumentUXContext()

  const { filteredOcrResults } = useOcrResults()
  const {
    ocrResultRefsMap,
    getLinkedDocumentsForResult,
    linkedSystemDocumentEntryIds,
    isLoadingRefs,
  } = useOcrRefsCount(filteredOcrResults)

  const {
    documents: linkedSystemDocuments,
    isLoading: isLoadingLinkedSystemDocuments,
  } = useSystemDocumentsByBqIds(linkedSystemDocumentEntryIds)

  const entryIdToBqDocumentId = useMemo(() => {
    const map = new Map<string, string>()
    for (const entry of linkedSystemDocuments ?? []) {
      const bqId = getSystemDocumentBqId(entry)
      if (bqId) {
        map.set(entry.entry.id, bqId)
      }
    }
    return map
  }, [linkedSystemDocuments])

  const resolveBqDocumentIdFromSystemEntryId = useCallback(
    (systemEntryId: string): string | undefined =>
      entryIdToBqDocumentId.get(systemEntryId),
    [entryIdToBqDocumentId]
  )

  const navigateOrOpenDocument = useCallback(
    (
      documentId: string,
      forceNewTab: boolean,
      pageNumber: number = DEFAULT_TARGET_PAGE
    ) => {
      if (forceNewTab) {
        addDocumentTab({ documentId, pageNumber, selectedBoxIds: [] })
      } else {
        const newDocItem: DocumentHistoryItem = {
          type: 'document',
          documentId,
          pageNumber,
          selectedBoxIds: [],
          query: '',
        }
        pushToHistory(activeTabId, newDocItem)
      }
    },
    [addDocumentTab, pushToHistory, activeTabId]
  )

  const showLinkedDocsTable = useCallback(
    (documentIds: string[], referenceText: string, forceNewTab: boolean) => {
      setGlobalPagination({ pageIndex: 0, pageSize: 200 })
      const linkedDocsItem: SearchHistoryItem = {
        type: 'search',
        searchState: {
          contentFilter: 'documents',
          searchMode: 'normal',
          viewMode: 'table',
          query: '',
          filters: [],
          sorting: [{ id: 'documentTitle', desc: false }],
          pagination: { pageIndex: 0, pageSize: 200 },
          selectedDocumentId: null,
        },
        linkedDocuments: {
          referenceText,
          documentIds,
        },
      }

      if (forceNewTab) {
        const newTab: DocumentTab = {
          id: `linked-docs-${Date.now()}`,
          history: [linkedDocsItem],
          currentIndex: 0,
        }
        addTab(newTab)
      } else {
        pushToHistory(activeTabId, linkedDocsItem)
      }
    },
    [setGlobalPagination, pushToHistory, activeTabId, addTab]
  )

  /**
   * Navigates to linked documents for a given OCR result.
   * Returns true if navigation was handled, false otherwise.
   */
  const navigateToLinkedDocuments = useCallback(
    ({
      ocrResult,
      forceNewTab,
      showReferencesTableOnSelect,
    }: LinkedDocumentsNavigationParams): boolean => {
      const referenceText = ocrResult.text ?? ''
      const notifySuccess = (documentIds: string[]) => {
        options?.onNavigationSuccess?.({ documentIds, referenceText })
      }

      if (ocrResult.type === DocumentViewerAssetType.OFF_PAGE_CONNECTOR) {
        if (options?.isLoadingOffPageConnector) return false
        const targets = resolveOffPageConnectorTargets({
          strategy: options?.resolverStrategy,
          offPageConnectorDocuments: options?.offPageConnectorDocuments ?? [],
        })

        if (targets.length === 0) return false

        if (targets.length > 1) {
          if (allCandidatesHaveRevisionCodes(targets)) {
            const latestTarget = getLatestCandidateByRevision(targets)
            if (!latestTarget) return false
            navigateOrOpenDocument(
              latestTarget.documentId,
              forceNewTab,
              latestTarget.pageNumber
            )
            notifySuccess([latestTarget.documentId])
            return true
          }

          const documentIds = targets.map((target) => target.documentId)
          showLinkedDocsTable(documentIds, referenceText, forceNewTab)
          notifySuccess(documentIds)
          return true
        }

        navigateOrOpenDocument(
          targets[0].documentId,
          forceNewTab,
          targets[0].pageNumber
        )
        notifySuccess([targets[0].documentId])
        return true
      }

      const linkedDocs = getLinkedDocumentsForResult(ocrResult)
      if (!linkedDocs.length) return false
      if (isLoadingLinkedSystemDocuments) return false

      const linkedSystemEntryIds = [
        ...new Set(linkedDocs.map((doc) => doc.entry.tags['system-document'])),
      ].filter(Boolean)

      if (linkedSystemEntryIds.length === 0) return false

      const linkedBqDocumentIds = linkedSystemEntryIds
        .map((entryId) => resolveBqDocumentIdFromSystemEntryId(entryId))
        .filter((id): id is string => !!id)

      if (linkedBqDocumentIds.length === 0) return false

      if (
        linkedSystemEntryIds.length === 1 &&
        linkedBqDocumentIds.length === 1 &&
        !showReferencesTableOnSelect
      ) {
        const pageNumber = resolveKnowledgeDocumentTargetPage(
          linkedDocs,
          linkedSystemEntryIds[0]
        )
        navigateOrOpenDocument(linkedBqDocumentIds[0], forceNewTab, pageNumber)
        notifySuccess(linkedBqDocumentIds)
        return true
      }

      showLinkedDocsTable(linkedBqDocumentIds, referenceText, forceNewTab)
      notifySuccess(linkedBqDocumentIds)
      return true
    },
    [
      getLinkedDocumentsForResult,
      isLoadingLinkedSystemDocuments,
      navigateOrOpenDocument,
      resolveBqDocumentIdFromSystemEntryId,
      showLinkedDocsTable,
      options,
    ]
  )

  const hasLinkedDocuments = useCallback(
    (ocrResult: OcrResultWithPage): boolean => {
      const key = ocrResultMatchKey(ocrResult.id, ocrResult.pageNumber)
      const refsCount = ocrResultRefsMap.get(key) ?? 0

      if (refsCount > 0) return true
      if (ocrResult.type === DocumentViewerAssetType.OFF_PAGE_CONNECTOR) {
        if (options?.isLoadingOffPageConnector) return false
        const linkedDocuments = options?.offPageConnectorDocuments ?? []
        return linkedDocuments.length > 0
      }

      return false
    },
    [
      ocrResultRefsMap,
      options?.offPageConnectorDocuments,
      options?.isLoadingOffPageConnector,
    ]
  )

  return {
    navigateToLinkedDocuments,
    hasLinkedDocuments,
    ocrResultRefsMap,
    getLinkedDocumentsForResult,
    navigateOrOpenDocument,
    showLinkedDocsTable,
    isLoadingRefs: isLoadingRefs || isLoadingLinkedSystemDocuments,
  }
}
